import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";

/**
 * Live preview of the next geoVINUM Journey email.
 *
 * The Journey is a SINGLE combined email containing one section per enabled
 * content type. The iframe below auto-refreshes whenever `selectedTypes`
 * changes so the user always sees exactly what will be sent — no manual
 * "generate" button needed. The remaining action is "send a test to my inbox"
 * to verify the real rendering.
 *
 * Props:
 *  - selectedTypes: string[] — currently-checked types in the parent form.
 */
export default function JourneyPreview({ selectedTypes }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [err, setErr] = useState("");

  const activeTypes = (Array.isArray(selectedTypes) && selectedTypes.length ? selectedTypes : ["info"])
    .filter((t) => ["info", "appellation", "producer", "cepage"].includes(t));
  const key = activeTypes.join(",");

  useEffect(() => {
    if (!key) return;
    let alive = true;
    (async () => {
      setBusy(true); setErr("");
      try {
        const r = await api.get("/me/notifications/journey/preview", { params: { types: key } });
        if (alive) setPreview(r.data);
      } catch {
        if (alive) setErr("Impossible de générer l'aperçu. Réessayez dans un instant.");
      } finally {
        if (alive) setBusy(false);
      }
    })();
    return () => { alive = false; };
  }, [key]);

  const testSend = async () => {
    setSending(true);
    try {
      const r = await api.post("/me/notifications/journey/test-send", { types: activeTypes });
      toast.success(`Email de test envoyé à ${r.data.recipient} (${(r.data.sections || []).length} section${(r.data.sections || []).length > 1 ? "s" : ""})`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Envoi impossible");
    } finally { setSending(false); }
  };

  return (
    <div className="mt-5 pt-4 border-t border-[#1F3A5F]/8" data-testid="journey-preview-block">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] uppercase tracking-widest text-[#8B95A1] font-bold">Aperçu de votre prochain email</div>
        <button
          onClick={testSend}
          disabled={sending || busy}
          data-testid="journey-preview-testsend"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold bg-[#E5276A] text-white hover:bg-[#C01661] disabled:opacity-60"
          title="Envoyer un exemplaire à mon adresse email pour vérifier le rendu réel"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
          Envoyer un test à mon email
        </button>
      </div>

      {err && <div className="mt-3 text-xs text-red-600">{err}</div>}

      {busy && !preview && (
        <div className="mt-3 py-8 text-center text-[#8B95A1]" data-testid="journey-preview-loading">
          <Loader2 className="w-5 h-5 animate-spin inline-block" />
        </div>
      )}

      {preview && (
        <div className="mt-3 rounded-xl border border-[#1F3A5F]/12 bg-white overflow-hidden relative" data-testid="journey-preview-panel">
          {busy && (
            <div className="absolute inset-0 bg-white/60 backdrop-blur-sm flex items-center justify-center z-10">
              <Loader2 className="w-5 h-5 animate-spin text-[#E5276A]" />
            </div>
          )}
          <iframe
            title="Journey email preview"
            srcDoc={preview.html}
            sandbox=""
            className="w-full h-[620px] border-0 bg-white"
            data-testid="journey-preview-iframe"
          />
        </div>
      )}
    </div>
  );
}
